import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FilterIcon from '@mui/icons-material/FilterListOutlined';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import RadiologyTable from './RadiologyTable';
import RadiologyTableMobile from './RadiologyTableMobile';
import FilterRadiologyModal from './FilterRadiologyModal';
import RadiologyModal from './RadiologyModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchRadiology,
  updateRadiology,
  deleteRadiology,
  addRadiology,
} from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './radiology.scss';

const RadiologyTableContainer = (props) => {
  const {
    radiology = [],
    distributors,
    createRadiology,
    updateRadiology,
    deleteRadiology,
    fetchRadiology,
    withButton,
    toggleSidebar,
    countries,
    companies,
    currentUser,
    users,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedRadiology, setSelectedRadiology] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ companies: [], countries: [] });

  const onEditClick = (row) => {
    setSelectedRadiology(row);
    toggleUpdateModal(true);
  };

  const onRadiologyCreate = async (formObj) => {
    try {
      setLoading(true);

      await createRadiology(formObj);
      await fetchRadiology();
      setSuccessMessage('New radiology successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRadiologyUpdate = async (formObj) => {
    if (selectedRadiology) {
      try {
        setLoading(true);

        await updateRadiology(selectedRadiology?.id, formObj);
        await fetchRadiology();
        setSuccessMessage('Radiology successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteRadiology = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteRadiology(selectedId);
        await fetchRadiology();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      companies: filters.companies || [],
      countries: filters.countries || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const filteredRadiology = useMemo(() => {
    let filtered = radiology;

    if (search) {
      const q = search.toLowerCase();
      filtered = radiology.filter((m) => m?.name?.toLowerCase()?.includes(q));
    }

    if (filterState?.companies?.length) {
      filtered = filtered?.filter((r) => filterState?.companies?.map((c) => c.id)?.includes(r.company));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((r) => filterState?.countries?.includes(r.country));
    }

    return filtered;
  }, [radiology, search, filterState]);

  const list = useMemo(() => {
    let result = filteredRadiology;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredRadiology, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Radiology
                  <Box className="badge">{radiology.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add and manage radiology
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text='Add Radiology'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {radiology.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {filterState &&
                (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                  && (
                    <div className="filter-list">
                      <FiltersIcon color="secondary" />
                      {Object.keys(filterState).map((key) => (
                        <div key={key} className="filter-list-item-container">
                          {filterState[key].map((value) => (
                            <div key={`${key}-${value}`} className="filter-list-item">
                              <div />
                              <div className="filter-list-item-value">{key === 'companies' ? value?.name : value}</div>
                              <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
              }
              <RadiologyTable
                radiology={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredRadiology?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                companies={companies}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <RadiologyTableMobile radiology={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Radiology</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteRadiology}
        title="Delete Radiology"
        text="Are you sure you want to delete this radiology?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <RadiologyModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onRadiologyCreate}
        countries={countries}
        loading={loading}
        companies={companies}
        usersList={users}
        currentUser={currentUser}
      />

      <RadiologyModal
        open={showUpdateModal}
        onClose={() => {
          toggleUpdateModal(false);
          setSelectedRadiology(undefined);
        }}
        onSubmit={onRadiologyUpdate}
        countries={countries}
        mode='update'
        initialValues={selectedRadiology}
        loading={loading}
        companies={companies}
        usersList={users}
        currentUser={currentUser}
      />

      <FilterRadiologyModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
        companies={companies}
        currentUser={currentUser}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteRadiology}
        title="Delete Radiology?"
        text="Are you sure you want to delete this radiology? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

RadiologyTableContainer.propTypes = {
  radiology: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
    currentUser: state.user.currentUser,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchRadiology: () => dispatch(fetchRadiology()),
    createRadiology: (radiologyData) => dispatch(addRadiology(radiologyData)),
    updateRadiology: (id, radiologyData) => dispatch(updateRadiology(id, radiologyData)),
    deleteRadiology: (id) => dispatch(deleteRadiology(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RadiologyTableContainer);
